uniform vec2 resolution;
uniform float time;
varying vec2 vUv;

// https://www.shadertoy.com/view/Dl2GWy
// Smaller Number Printing - @P_Malin
// Creative Commons CC0 1.0 Universal (CC-0)

// Feel free to modify, distribute or use in commercial code, just don't hold me liable for anything bad that happens!
// If you use this code and want to give credit, that would be nice but you don't have to.

// I first made this number printing code in https://www.shadertoy.com/view/4sf3RN
// It started as a silly way of representing digits with rectangles.
// As people started actually using this in a number of places I thought I would try to condense the 
// useful function a little so that it can be dropped into other shaders more easily,
// just snip between the perforations below.
// Also, the licence on the previous shader was a bit restrictive for utility code.
//
// Disclaimer: The values printed may not be accurate!
// Accuracy improvement for fractional values taken from TimoKinnunen https://www.shadertoy.com/view/lt3GRj

// ---- 8< ---- GLSL Number Printing - @P_Malin ---- 8< ----
// Creative Commons CC0 1.0 Universal (CC-0) 
// https://www.shadertoy.com/view/4sBSWW

float DigitBin(const int x) {
    return x == 0 ? 480599.0 : x == 1 ? 139810.0 : x == 2 ? 476951.0 : x == 3 ? 476999.0 : x == 4 ? 350020.0 : x == 5 ? 464711.0 : x == 6 ? 464727.0 : x == 7 ? 476228.0 : x == 8 ? 481111.0 : x == 9 ? 481095.0 : 0.0;
}

float PrintValue(vec2 vStringCoords, float fValue, float fMaxDigits, float fDecimalPlaces) {
    if((vStringCoords.y < 0.0) || (vStringCoords.y >= 1.0))
        return 0.0;

    bool bNeg = (fValue < 0.0);
    fValue = abs(fValue);

    float fLog10Value = log2(abs(fValue)) / log2(10.0);
    float fBiggestIndex = max(floor(fLog10Value), 0.0);
    float fDigitIndex = fMaxDigits - floor(vStringCoords.x);
    float fCharBin = 0.0;
    if(fDigitIndex > (-fDecimalPlaces - 1.01)) {
        if(fDigitIndex > fBiggestIndex) {
            if((bNeg) && (fDigitIndex < (fBiggestIndex + 1.5)))
                fCharBin = 1792.0;
        } else {
            if(fDigitIndex == -1.0) {
                if(fDecimalPlaces > 0.0)
                    fCharBin = 2.0;
            } else {
                float fReducedRangeValue = fValue;
                if(fDigitIndex < 0.0) {
                    fReducedRangeValue = fract(fValue);
                    fDigitIndex += 1.0;
                }
                float fDigitValue = (abs(fReducedRangeValue / (pow(10.0, fDigitIndex))));
                fCharBin = DigitBin(int(floor(mod(fDigitValue, 10.0))));
            }
        }
    }
    return floor(mod((fCharBin / pow(2.0, floor(fract(vStringCoords.x) * 4.0) + (floor(vStringCoords.y * 5.0) * 4.0))), 2.0));
}

// ---- 8< -------- 8< -------- 8< -------- 8< ----
// End of: Smaller Number Printing - @P_Malin

#pragma glslify: snoise3 = require(glsl-noise/simplex/3d) 

vec4 cell(vec2 cellUv, vec2 cellResolution, ivec2 cellIndexPosition, int cellIndex, int cellCount) {

    vec3 cellColor = vec3(0.0);
    vec2 p = cellUv * cellResolution;
    float v = 0.0;
    float cellV = 0.0;

    float timeMultiplyer = 0.001;
    float cellDuration = 10.0;

    float x = float(cellIndex) * 1213.0;
    float y = float(cellIndex) * 113.0;
    cellV = snoise3(vec3(x, y, (time * timeMultiplyer)));
    v = snoise3(vec3(x, y, (time * timeMultiplyer) - (1.0 - cellUv.x * cellDuration)));

    cellColor = mix(cellColor, vec3(0.5), step(cellUv.y * 2.0 - 1.0, v));

    // Text
    // ====

	// Multiples of 4x5 work best
    float fontSize = 2.0;
    vec2 vFontSize = vec2(4.0 * fontSize, 8.0 * fontSize);

    // Print the index
    vec2 textPosition = vec2(0.0, cellResolution.y - vFontSize.y - 10.0);
    vec2 vStringCharCoords = (p - textPosition) / vFontSize;
    float isDigit = PrintValue(vStringCharCoords, float(cellIndex + 1), 1.0, 0.0);
    cellColor = mix(cellColor, vec3(1.0, 1.0, 1.0), isDigit);

    // Print the cell value
    textPosition = vec2(vFontSize.x * 2.0, cellResolution.y - vFontSize.y - 10.0);
    vStringCharCoords = (p - textPosition) / vFontSize;
    isDigit = PrintValue(vStringCharCoords, cellV, 1.0, 10.0);
    cellColor = mix(cellColor, vec3(0.0, 1.0, 0.0), isDigit);

    return vec4(cellColor, 1.0);
}

float withinSmoothed(float minIn, float maxIn, float v, float smoothingOutside) {
    float _min = min(minIn, maxIn);
    float _max = max(minIn, maxIn);
    float isAboveMin = smoothstep(_min - smoothingOutside, _min, v);
    float isAboveMax = smoothstep(_max + smoothingOutside, _max, v);
    return min(isAboveMax, isAboveMin);
}

void main() {
    vec2 st = gl_FragCoord.xy / resolution.xy;
    vec2 uv = vUv;

    const int numberRows = 4;
    const int numberColumns = 2;
    const int cellCount = numberRows * numberColumns;
    vec2 cellResolution = resolution.xy / vec2(float(numberColumns), float(numberRows));
    int index = 0;
    vec4 color = vec4(0.37, 1.0, 0.0, 1.0);

    float columnWidth = 1.0 / float(numberColumns);
    float rowHeight = 1.0 / float(numberRows);

    for(int row = numberRows - 1; row >= 0; row--) {
        for(int column = 0; column < numberColumns; column++) {

            vec2 cellUv = vec2((uv.x * float(numberColumns)) - float(column), (uv.y * float(numberRows)) - float(row));

            float inColumn = withinSmoothed(0.0, 1.0, cellUv.x, 0.005);
            float inRow = withinSmoothed(0.0, 1.0, cellUv.y, 0.005);

            // Show a grey scale for each cell
            //float indexScalar = float(index) / float(cellCount-1);
            //vec4 cellColor = vec4(vec3(indexScalar), 1.0);

            vec4 cellColor = cell(cellUv, cellResolution, ivec2(row, column), index, cellCount);

            color = mix(color, cellColor, inColumn * inRow);

            index++;
        }
    }

    gl_FragColor = color;

    // vec3 vColour = vec3(0.0);

	// // Multiples of 4x5 work best
    // float fontSize = 4.0;
	// vec2 vFontSize = vec2(4.0 * fontSize, 8.0 * fontSize);

	// // Print a custom value
	// vec2 vPixelCoord1 = vec2(5.0, 5.0);
    // float customValueToPrint = 7.0;

    // float customDigit = PrintValue( (gl_FragCoord.xy - vPixelCoord1) / vFontSize, customValueToPrint, 4.0, 4.0);
	// vColour = mix( vColour, vec3(0.0, 1.0, 1.0), customDigit);

    // gl_FragColor = vec4(vColour, 1.0);
}